// A recorded video takes ~1-2 min to process. When a metric has no value yet we
// treat it as still "calculating" for a short window after the recording was
// taken; past that window we assume something went wrong and fall back to the
// empty placeholder ("--"). An out-of-range VALUE is not "no value" — it renders
// normally (as "--" via the validated-range formatter), never as calculating.
export const METRIC_PENDING_WINDOW_MS = 5 * 60 * 1000;

export function toRecordingMs(recordingTimestamp) {
  if (recordingTimestamp == null) return null;
  if (recordingTimestamp instanceof Date) {
    const ms = recordingTimestamp.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof recordingTimestamp === "number") {
    return Number.isFinite(recordingTimestamp) ? recordingTimestamp : null;
  }
  const ms = new Date(recordingTimestamp).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function hasMetricValue(value) {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * True only within METRIC_PENDING_WINDOW_MS of the recording being taken. Use
 * this when the caller already knows the metric has no value (e.g. a non-numeric
 * quality status), so the "has value" check lives with the caller.
 */
export function isWithinPendingWindow(
  recordingTimestamp,
  { now = Date.now(), windowMs = METRIC_PENDING_WINDOW_MS } = {},
) {
  const ms = toRecordingMs(recordingTimestamp);
  return ms != null && now - ms < windowMs;
}

/**
 * A metric with no numeric value is "pending" (still calculating) only within
 * METRIC_PENDING_WINDOW_MS of the recording being taken.
 */
export function isMetricPending(value, recordingTimestamp, opts = {}) {
  if (hasMetricValue(value)) return false;
  return isWithinPendingWindow(recordingTimestamp, opts);
}
